const FAV_KEY = 'favorites';

// ── Storage helpers ──────────────────────────────────────────────────────────

function getFavs() {
  try { return JSON.parse(localStorage.getItem(FAV_KEY)) || []; } catch (e) { return []; }
}

function setFavs(arr) {
  localStorage.setItem(FAV_KEY, JSON.stringify(arr));
  document.dispatchEvent(new CustomEvent('ps:favorites:changed', {
    detail: { ids: arr.map(x => typeof x === 'object' ? x.id : x) }
  }));
}

function getFavObjects() {
  return getFavs().filter(x => x && typeof x === 'object');
}

function isFavById(id) {
  return getFavs().some(x => String(typeof x === 'object' ? x.id : x) === String(id));
}

function removeFav(id) {
  setFavs(getFavs().filter(x => String(typeof x === 'object' ? x.id : x) !== String(id)));
}

// ── Header badge ─────────────────────────────────────────────────────────────

function updateHeaderBadge() {
  const icon = document.querySelector('#fav-icon-bubble');
  if (!icon) return;
  const count = getFavObjects().length;

  if (count > 0) {
    icon.style.display = 'flex';
    let badge = icon.querySelector('.header-block__fav-count');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'header-block__fav-count';
      icon.appendChild(badge);
    }
    badge.textContent = count;
  } else {
    icon.style.display = 'none';
  }

  const dc = document.getElementById('fav-drawer-count');
  if (dc) dc.textContent = count > 0 ? `${count} ${count === 1 ? 'item' : 'items'}` : '';
}

// ── Product card UI ──────────────────────────────────────────────────────────

function applyFavUI(scope) {
  const ctx = scope || document;
  const favs = getFavs();
  const sel = '.products-list-block__slide[data-product-id], .product-card[data-product-id], .product-favorites-wrapper[data-product-id]';
  ctx.querySelectorAll(sel).forEach(slide => {
    const pid = slide.dataset.productId;
    const active = favs.some(x => String(typeof x === 'object' ? x.id : x) === String(pid));
    const icon = slide.querySelector('.products-list-block__favourites:not(.products-list-block__favourites--selected)');
    const iconSel = slide.querySelector('.products-list-block__favourites--selected');
    if (icon) icon.style.display = active ? 'none' : '';
    if (iconSel) iconSel.style.display = active ? 'block' : 'none';
    slide.classList.toggle('is-fav', active);
  });
}

function buildProductData(slide) {
  const variants = [];
  slide.querySelectorAll('.products-list-block__product-form-options label').forEach(label => {
    const radio = label.querySelector('input[type="radio"]');
    const nameSpan = label.querySelector('div span');
    const priceSpan = label.querySelector('span:last-of-type');
    if (radio?.value && nameSpan) {
      variants.push({ id: radio.value, name: nameSpan.textContent.trim(), price: priceSpan?.textContent.trim() || '' });
    }
  });
  return {
    id: slide.dataset.productId,
    title: slide.dataset.productTitle || '',
    image: slide.dataset.productImage || '',
    price_formatted: slide.dataset.productPriceFmt || '',
    variant_id: slide.dataset.productVariant || '',
    url: slide.dataset.productUrl || slide.getAttribute('href') || '',
    variants: variants.length > 1 ? variants : [],
  };
}

function toggleFavFromSlide(slide) {
  const pid = slide.dataset.productId;
  if (!pid) return;
  if (isFavById(pid)) {
    removeFav(pid);
  } else {
    const favs = getFavs();
    favs.push(buildProductData(slide));
    setFavs(favs);
  }
  updateHeaderBadge();
  applyFavUI();
}

// ── Direct listeners on heart icons ─────────────────────────────────────────
// Event delegation inside <a> tags is unreliable; bind directly to each icon.

function handleIconClick(e) {
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
  const slide = this.closest('.products-list-block__slide, .product-card, .product-favorites-wrapper');
  if (slide) toggleFavFromSlide(slide);
}

function bindFavIcons(root) {
  const ctx = root || document;
  ctx.querySelectorAll('.products-list-block__favourites:not([data-fav-bound])').forEach(icon => {
    icon.setAttribute('data-fav-bound', '1');
    icon.addEventListener('click', handleIconClick);
    icon.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') handleIconClick.call(icon, e);
    });
  });
}

// ── Sync selected variant radio → slide data attributes ─────────────────────

document.addEventListener('change', function (e) {
  const radio = e.target;
  if (radio.type !== 'radio' || !radio.closest('.products-list-block__product-form')) return;

  const slide = radio.closest('.products-list-block__slide, .product-card');
  if (!slide) return;

  slide.dataset.productVariant = radio.value;

  // Read price from the radio label's price span if present
  const label = radio.closest('label');
  if (label) {
    const priceSpan = label.querySelector('span:last-child');
    if (priceSpan) slide.dataset.productPriceFmt = priceSpan.textContent.trim();
  }
});

// Watch containers for dynamically-loaded slides (Swiper, AJAX, etc.)
if ('MutationObserver' in window) {
  const mo = new MutationObserver((mutations) => {
    let needBind = false;
    for (const m of mutations) {
      if (m.addedNodes.length) { needBind = true; break; }
    }
    if (needBind) { bindFavIcons(); applyFavUI(); }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

// ── Drawer custom element ────────────────────────────────────────────────────

class FavouritesDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());

    document.addEventListener('ps:favorites:changed', () => {
      updateHeaderBadge();
      if (this.classList.contains('active')) this._renderItems();
    });

    window.addEventListener('storage', (e) => {
      if (e.key !== FAV_KEY) return;
      updateHeaderBadge();
      if (this.classList.contains('active')) this._renderItems();
    });
  }

  connectedCallback() {
    const overlay = this.querySelector('.fav-drawer__overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.addEventListener('click', (e) => {
      if (!e.target.closest('.drawer__inner')) this.close();
    });

    updateHeaderBadge();
  }

  open() {
    this._renderItems();
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener('transitionend', () => {
      const focusEl = this.querySelector('.drawer__close');
      if (focusEl) focusEl.focus();
    }, { once: true });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  async _fetchVariants(p) {
    if (p.variants && p.variants.length > 1) return p;
    const handle = (p.url || '').replace('/products/', '').split('?')[0].split('#')[0];
    if (!handle) return p;
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return p;
      const data = await res.json();
      if (!data.variants || data.variants.length < 2) return p;
      const variants = data.variants.map(v => ({
        id: String(v.id),
        name: v.option1 || v.title,
        price: v.price ? ('$' + (v.price / 100).toFixed(2)) : ''
      }));
      const updated = { ...p, variants };
      const favs = getFavs().map(x => {
        const id = typeof x === 'object' ? x.id : x;
        return String(id) === String(p.id) ? updated : x;
      });
      setFavs(favs);
      return updated;
    } catch (e) { return p; }
  }

  async _renderItems() {
    let favs = getFavObjects();
    const container = this.querySelector('#FavDrawer-Items');
    const empty = this.querySelector('#FavDrawer-Empty');
    const footer = this.querySelector('#FavDrawer-Footer');
    if (!container) return;

    if (favs.length === 0) {
      container.innerHTML = '';
      empty && empty.removeAttribute('hidden');
      footer && footer.setAttribute('hidden', '');
      return;
    }

    empty && empty.setAttribute('hidden', '');
    footer && footer.removeAttribute('hidden');

    favs = await Promise.all(favs.map(p => this._fetchVariants(p)));

    container.innerHTML = favs.map(p => {
      const hasVariants = p.variants && p.variants.length > 1;
      const firstVariantId = hasVariants ? p.variants[0].id : p.variant_id;
      const variantForm = hasVariants ? `
        <div class="fav-item-card__form">
          <div class="fav-item-card__qty-row">
            <span>QUANTITY</span>
            <div class="fav-item-card__qty-controls">
              <button class="fav-qty-dec" type="button">-</button>
              <input type="number" class="fav-qty-val" value="1" min="1">

                            <button class="fav-qty-inc" type="button">+</button>

            </div>
          </div>
          ${p.variants.map((v, i) => `
            <label class="fav-item-card__variant-row">
              <div>
                <input type="radio" name="fav-variant-${this._esc(p.id)}" value="${this._esc(v.id)}"${i === 0 ? ' checked' : ''}>
                <span>${this._esc(v.name)}</span>
              </div>
              <span>${this._esc(v.price)}</span>
            </label>
          `).join('')}
        </div>
      ` : '';
      return `
        <div class="fav-item-card${hasVariants ? ' has-variants' : ''}" data-id="${this._esc(p.id)}">
          <div class="fav-item-card__header">
            <a href="${this._esc(p.url)}" class="fav-item-card__image">
              <img src="${this._esc(p.image)}" alt="${this._esc(p.title)}" loading="lazy">
            </a>
            <div class="fav-item-card__details">
              <p class="fav-item-card__title">${this._esc(p.title)}</p>
              <p class="fav-item-card__price">${this._esc(p.price_formatted)}</p>
              <button class="fav-item-card__remove" type="button"
                data-product-id="${this._esc(p.id)}"
                aria-label="Remove from favourites">&times;</button>
            </div>
          </div>
          ${variantForm}
          <button class="fav-item-card__add" type="button"
            data-variant-id="${this._esc(firstVariantId)}"
            data-product-id="${this._esc(p.id)}">
            ADD TO BASKET
          </button>
        </div>
      `;
    }).join('');

    container.querySelectorAll('.fav-item-card__add').forEach(btn => {
      btn.addEventListener('click', () => this._addToCart(btn));
    });

    container.querySelectorAll('.fav-item-card__remove').forEach(btn => {
      btn.addEventListener('click', () => {
        removeFav(btn.dataset.productId);
        this._renderItems();
        updateHeaderBadge();
      });
    });

    container.querySelectorAll('.fav-qty-inc').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.fav-item-card__qty-controls').querySelector('.fav-qty-val');
        input.value = Math.max(1, parseInt(input.value || 1) + 1);
      });
    });

    container.querySelectorAll('.fav-qty-dec').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.fav-item-card__qty-controls').querySelector('.fav-qty-val');
        input.value = Math.max(1, parseInt(input.value || 1) - 1);
      });
    });

    container.querySelectorAll('.fav-item-card__variant-row input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', () => {
        const card = radio.closest('.fav-item-card');
        const addBtn = card?.querySelector('.fav-item-card__add');
        if (addBtn) addBtn.dataset.variantId = radio.value;
      });
    });
  }

  async _addToCart(btn) {
    const card = btn.closest('.fav-item-card');
    const checkedRadio = card?.querySelector('.fav-item-card__variant-row input[type="radio"]:checked');
    const variantId = checkedRadio?.value || btn.dataset.variantId;
    const productId = btn.dataset.productId;
    const qtyInput = card?.querySelector('.fav-qty-val');
    const quantity = parseInt(qtyInput?.value || '1', 10) || 1;

    if (!variantId) return;

    btn.disabled = true;
    const original = btn.textContent.trim();
    btn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity }),
      });

      if (res.ok) {
        removeFav(productId);
        this._renderItems();
        updateHeaderBadge();
        this.close();
        const cartEl = document.querySelector('.cart');
        if (cartEl) {
          cartEl.classList.add('active');
          document.body.classList.add('cart-open');
        }
        if (typeof updateCartDisplay === 'function') {
          await updateCartDisplay();
        }
        try {
          const cartData = await (await fetch('/cart.js')).json();
          if (typeof updateCartCount === 'function') updateCartCount(cartData.item_count);
        } catch (_) { }
      } else {
        const errText = await res.text().catch(() => res.status);
        console.error('[fav] cart/add.js failed', res.status, errText);
        btn.disabled = false;
        btn.textContent = original;
      }
    } catch (err) {
      console.error('[fav] cart/add.js error', err);
      btn.disabled = false;
      btn.textContent = original;
    }
  }

  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

customElements.define('favourites-drawer', FavouritesDrawer);

// ── Sync selected variant to product-favorites-wrapper on product page ───────

document.addEventListener('variant:change', function (e) {
  const detail = e.detail || {};
  const v = detail.variant || detail.selectedVariant || detail.productVariant || null;
  if (!v || !v.id) return;

  document.querySelectorAll('.product-favorites-wrapper[data-product-id]').forEach(wrapper => {
    wrapper.dataset.productVariant = v.id;

    // update price: read from rendered DOM price element
    const priceEl = document.querySelector(
      '.product-overview-block__price-current, .price__sale .price-item--sale, .price-item--regular'
    );
    if (priceEl) {
      wrapper.dataset.productPriceFmt = priceEl.textContent.trim();
    } else if (v.price != null) {
      wrapper.dataset.productPriceFmt = (v.price / 100).toLocaleString('en-US', {
        style: 'currency', currency: 'USD'
      });
    }
  });
});

// ── Events from other sections ───────────────────────────────────────────────

['shopify:section:load', 'shopify:section:select', 'ps:favorites:changed'].forEach(ev =>
  document.addEventListener(ev, () => { bindFavIcons(); applyFavUI(); })
);

document.addEventListener('click', (e) => {
  const btn = e.target.closest('#fav-icon-bubble');
  if (!btn) return;
  e.preventDefault();
  const drawer = document.querySelector('favourites-drawer');
  if (drawer) drawer.open();
});

document.addEventListener('click', (e) => {
  const link = e.target.closest('.save-to-favorites-link');
  if (!link) return;
  e.preventDefault();
  const wrapper = document.querySelector('.product-favorites-wrapper[data-product-id]');
  if (wrapper) toggleFavFromSlide(wrapper);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bindFavIcons();
    applyFavUI();
    updateHeaderBadge();
  });
} else {
  bindFavIcons();
  applyFavUI();
  updateHeaderBadge();
}
